import { ReactNode, useState, useCallback, useMemo } from 'react';

import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';

import { RmpcTattooShort } from '@/api/integrations/rmpc/types/types';
import { GeneralDictionaryType } from '@/api/types/common';

import { queryKeys } from '@/api/queryKeys';

import api from '@/api';

import type { TattooSearchFormData } from '../model/types';

import { TattooSearchContext, TattooSearchContextValue } from './TattooSearchContext';

export const PAGE_SIZE = 10;
const PAGE = 1;

interface TattooSearchProviderProps {
  children: ReactNode;
}

export const TattooSearchProvider: React.FC<TattooSearchProviderProps> = ({ children }) => {
  const [page, setPage] = useState(PAGE);
  const [searchParams, setSearchParams] = useState<TattooSearchFormData | null>(null);

  const { data: categoriesData, isLoading: isCategoriesLoading } = useQuery({
    queryKey: queryKeys.rmpc.tattooCategories(),
    queryFn: () => api.rmpc.dictionaries.getTattooCategories(),
  });

  const { data: bodyLocationsData, isLoading: isBodyLocationsLoading } = useQuery({
    queryKey: queryKeys.rmpc.tattooBodyLocations(),
    queryFn: () => api.rmpc.dictionaries.getTattooBodyLocations(),
  });

  const {
    data: initialTattooResults,
    isLoading: isInitialLoading,
    isFetching: isInitialFetching,
    isFetched: hasInitialTattooResultsBeenFetched,
  } = useQuery({
    queryKey: queryKeys.rmpc.tattooSearch(page),
    queryFn: () => api.rmpc.getTattooSearch({ page, per_page: PAGE_SIZE }),
    enabled: !searchParams,
    placeholderData: keepPreviousData,
  });

  const {
    data: filteredTattooResults,
    isLoading: isFilteredLoading,
    isFetching: isFilteredFetching,
    isFetched: hasFilteredBeenFetched,
  } = useQuery({
    queryKey: queryKeys.rmpc.tattooSearch(page, searchParams),
    queryFn: () => api.rmpc.postTattooSearch({ page, per_page: PAGE_SIZE }, searchParams!),
    enabled: !!searchParams,
    placeholderData: keepPreviousData,
  });

  const {
    mutate: loadTattooDetails,
    data: tattooDetailsData,
    isPending: isDetailsLoading,
    reset: resetTattooDetails,
  } = useMutation({
    mutationFn: (number: string) => api.rmpc.getTattooDetails(number),
  });

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSearch = useCallback((data: TattooSearchFormData) => {
    setSearchParams(data);
    setPage(1);
  }, []);

  const useFiltered = !!searchParams;
  const resultData = useFiltered ? filteredTattooResults : initialTattooResults;
  const result = useMemo(() => resultData?.data ?? [], [resultData?.data]);
  const total = useMemo(() => resultData?.meta?.total ?? 0, [resultData?.meta?.total]);

  const hasBeenFetched = hasInitialTattooResultsBeenFetched || hasFilteredBeenFetched;

  const handleDetails = useCallback(
    (item: RmpcTattooShort): void => {
      loadTattooDetails(item.number);
    },
    [loadTattooDetails],
  );

  const handleModalClose = useCallback((): void => {
    resetTattooDetails();
  }, [resetTattooDetails]);

  const handleClear = useCallback((): void => {
    setSearchParams(null);
    setPage(1);
  }, []);

  const getContextDictionaries = useCallback((): Record<string, GeneralDictionaryType[]> => {
    const dicts: Record<string, GeneralDictionaryType[]> = {};
    if (categoriesData?.data) {
      dicts.TATTOO_CATEGORIES = categoriesData.data;
    }
    if (bodyLocationsData?.data) {
      dicts.TATTOO_BODY_LOCATIONS = bodyLocationsData.data;
    }
    return dicts;
  }, [categoriesData?.data, bodyLocationsData?.data]);

  const contextValue: TattooSearchContextValue = useMemo(
    () => ({
      categories: categoriesData?.data ?? null,
      bodyLocations: bodyLocationsData?.data ?? null,
      page,
      searchParams,
      result,
      total,
      tattooDetailsData: tattooDetailsData?.data ?? null,
      isLoading: isInitialLoading || isFilteredLoading,
      isFetching: isInitialFetching || isFilteredFetching,
      isDetailsLoading,
      isDictionariesLoading: isCategoriesLoading || isBodyLocationsLoading,
      hasBeenFetched,
      getContextDictionaries,
      handleSearch,
      handlePageChange,
      handleDetails,
      handleModalClose,
      handleClear,
    }),
    [
      categoriesData?.data,
      bodyLocationsData?.data,
      page,
      searchParams,
      result,
      total,
      tattooDetailsData?.data,
      isInitialLoading,
      isFilteredLoading,
      isInitialFetching,
      isFilteredFetching,
      isDetailsLoading,
      isCategoriesLoading,
      isBodyLocationsLoading,
      hasBeenFetched,
      getContextDictionaries,
      handleSearch,
      handlePageChange,
      handleDetails,
      handleModalClose,
      handleClear,
    ],
  );

  return <TattooSearchContext.Provider value={contextValue}>{children}</TattooSearchContext.Provider>;
};
